import { ReactProductCard } from '@/components/react/ui/cards/ReactProductCard';
import {
  SteppedModal,
  type ModalStep,
} from '@/components/react/ui/plp/modals/SteppedModal';
import { ReactBadge } from '@/components/react/ui/ReactBadge';
import { ReactImage } from '@/components/react/ui/ReactImage';
import type React from 'react';
import type { ComponentProps } from 'react';

interface PriceModalCardProps
  extends Pick<ComponentProps<typeof ReactProductCard>, 'badges' | 'image'> {
  heading?: string;
  subheading?: string;
  text: string;
  imgAlt: string;
}

const PriceModalCard: React.FC<PriceModalCardProps> = ({
  badges,
  heading,
  subheading,
  image,
  imgAlt,
  text,
}) => {
  return (
    <div className="group relative flex w-full flex-col gap-2">
      <div className="group relative flex w-full flex-col gap-4 overflow-clip rounded-sm">
        <div className="group relative flex w-full flex-col overflow-clip rounded-t-sm no-underline">
          <ReactImage
            {...image}
            alt={imgAlt}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="flex aspect-square flex-col items-center justify-between bg-cover bg-center bg-no-repeat p-3">
            {badges && (
              <div className="z-10 flex w-full flex-wrap items-start gap-3">
                {badges.map((badge, idx) => (
                  <ReactBadge key={idx} {...badge} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-start px-0">
        <div className="flex flex-col justify-start gap-1">
          {heading && (
            <span className="text-base leading-[150%] font-semibold">
              {heading}
            </span>
          )}
          {subheading && (
            <span className="text-base leading-[150%] font-semibold">
              {subheading}
            </span>
          )}
          {text && <div className="text-base leading-relaxed">{text}</div>}
        </div>
      </div>
    </div>
  );
};
interface PriceModalProps {
  title: string;
  modalBackText: string;
  modalCloseText: string;
  modalTipText: string;
  steps: {
    coverOptions: {
      title: string;
      description: string;
      tip: string;
      nextText: string;
      content: {
        imageWrapHardcover: PriceModalCardProps;
        softcover: PriceModalCardProps;
      };
    };
    size: {
      title: string;
      description: string;
      tip: string;
      nextText: string;
      content: {
        paperback: PriceModalCardProps;
        hardcover: PriceModalCardProps;
      };
    };
    paper: {
      title: string;
      description: string;
      tip: string;
      nextText: string;
      content: {
        premium: PriceModalCardProps;
        standard: PriceModalCardProps;
      };
    };
    pages: {
      title: string;
      description: string;
      tip: string;
      nextText: string;
      content: {
        minimum: PriceModalCardProps;
        additional: PriceModalCardProps;
      };
    };
    binding: {
      title: string;
      description: string;
      tip: string;
      nextText: string;
      content: {
        wireO: PriceModalCardProps;
        perfect: PriceModalCardProps;
        layflat: PriceModalCardProps;
      };
    };
  };
}

export const PriceModal: React.FC<PriceModalProps> = (props) => {
  const priceModalSteps: ModalStep[] = [
    {
      title: props.steps.coverOptions.title,
      description: props.steps.coverOptions.description,
      content: (
        <div className="flex flex-col gap-6 md:flex-row md:gap-3">
          <PriceModalCard
            {...props.steps.coverOptions.content.imageWrapHardcover}
          />
          <PriceModalCard {...props.steps.coverOptions.content.softcover} />
        </div>
      ),
      tip: {
        title: props.modalTipText,
        description: props.steps.coverOptions.tip,
      },
      nextText: props.steps.coverOptions.nextText,
    },
    {
      title: props.steps.size.title,
      description: props.steps.size.description,
      content: (
        <div className="flex flex-col gap-6 md:flex-row md:gap-3">
          <PriceModalCard {...props.steps.size.content.paperback} />
          <PriceModalCard {...props.steps.size.content.hardcover} />
        </div>
      ),
      tip: {
        title: props.modalTipText,
        description: props.steps.size.tip,
      },
      nextText: props.steps.size.nextText,
    },
    {
      title: props.steps.paper.title,
      description: props.steps.paper.description,
      content: (
        <div className="flex flex-col gap-6 md:flex-row md:gap-3">
          <PriceModalCard {...props.steps.paper.content.premium} />
          <PriceModalCard {...props.steps.paper.content.standard} />
        </div>
      ),
      tip: {
        title: props.modalTipText,
        description: props.steps.paper.tip,
      },
      nextText: props.steps.paper.nextText,
    },
    {
      title: props.steps.pages.title,
      description: props.steps.pages.description,
      content: (
        <div className="flex flex-col gap-6 md:flex-row md:gap-3">
          <PriceModalCard {...props.steps.pages.content.minimum} />
          <PriceModalCard {...props.steps.pages.content.additional} />
        </div>
      ),
      tip: {
        title: props.modalTipText,
        description: props.steps.pages.tip,
      },
      nextText: props.steps.pages.nextText,
    },
    {
      title: props.steps.binding.title,
      description: props.steps.binding.description,
      content: (
        <div className="flex flex-col gap-6 md:flex-row md:gap-3">
          <PriceModalCard {...props.steps.binding.content.wireO} />
          <PriceModalCard {...props.steps.binding.content.perfect} />
          <PriceModalCard {...props.steps.binding.content.layflat} />
        </div>
      ),
      tip: {
        title: props.modalTipText,
        description: props.steps.binding.tip,
      },
      nextText: props.steps.binding.nextText,
    },
  ];

  return (
    <SteppedModal
      title={props.title}
      modalBackText={props.modalBackText}
      modalTriggerText={props.title}
      modalCloseText={props.modalCloseText}
      steps={priceModalSteps}
    />
  );
};
